import { useState } from "react";
import { FileText, RefreshCw, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const HOOK_STYLE = { delivered: "bg-emerald-50 text-emerald-700", retrying: "bg-amber-50 text-amber-700", pending: "bg-sky-50 text-sky-700", failed: "bg-rose-50 text-rose-700" };

export function ConsentBadge({ lead }) {
  if (!lead.checkout) return null;
  return lead.whatsapp_consent
    ? <span className="sb-chip bg-emerald-50 text-emerald-700" data-testid={`lead-consent-${lead.id}`}><Check className="h-3 w-3" />WA consent</span>
    : <span className="sb-chip bg-[#F5F0E6] text-[#8C9BA5]" data-testid={`lead-consent-${lead.id}`}><X className="h-3 w-3" />No WA consent</span>;
}

export function Receipts({ lead }) {
  const [busy, setBusy] = useState("");
  const open = async (r) => {
    const w = window.open("", "_blank");
    setBusy(r.id);
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/receipts/${r.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (w) w.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { w?.close(); toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  if (!lead.receipts?.length) return <p className="text-xs text-[#8C9BA5]" data-testid="lead-no-receipts">No payment proof uploaded.</p>;
  return (
    <div className="space-y-1.5" data-testid="lead-receipts">
      {lead.receipts.map((r) => (
        <button key={r.id} onClick={() => open(r)} className="w-full flex items-center gap-2 rounded-xl border border-[#EADEC9] px-3 py-2 text-left text-xs hover:bg-[#FBF7EF]" data-testid={`lead-receipt-${r.id}`}>
          <FileText className="h-4 w-4 text-[#1DB99A] shrink-0" /><b>{r.reference}</b><span className="text-[#8C9BA5] truncate">{r.filename} · {new Date(r.uploaded_at).toLocaleString()}</span>
          <span className="ml-auto font-semibold text-[#179B80]">{busy === r.id ? "Opening…" : "View"}</span>
        </button>
      ))}
    </div>
  );
}

export function Webhooks({ lead, onChange }) {
  const retry = async (w) => {
    try { const { data } = await api.post(`/admin/webhooks/${w.id}/retry`); toast[data.status === "delivered" ? "success" : "error"](data.status === "delivered" ? "Delivered to Make.com" : `Still failing: ${data.last_error}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!lead.webhooks?.length) return <p className="text-xs text-[#8C9BA5]">No Make.com events for this order.</p>;
  return (
    <div className="space-y-1.5" data-testid="lead-webhooks">
      {lead.webhooks.map((w) => (
        <div key={w.id} className="rounded-xl border border-[#EADEC9] px-3 py-2 text-xs" data-testid={`lead-webhook-${w.event}`}>
          <div className="flex items-center gap-2"><b>{w.event}</b><span className={`sb-chip ${HOOK_STYLE[w.status]}`} data-testid={`lead-webhook-status-${w.event}`}>{w.status}</span><span className="text-[#8C9BA5]">{w.attempts} attempt{w.attempts === 1 ? "" : "s"}</span>
            {w.status !== "delivered" && <button onClick={() => retry(w)} className="ml-auto inline-flex items-center gap-1 font-semibold text-[#179B80]" data-testid={`lead-webhook-retry-${w.event}`}><RefreshCw className="h-3 w-3" />Retry now</button>}</div>
          {w.last_error && <div className="text-rose-600 mt-1 break-all">{w.last_error}</div>}
          {w.next_attempt_at && <div className="text-[#8C9BA5]">Next automatic retry {new Date(w.next_attempt_at).toLocaleString()}</div>}
        </div>
      ))}
    </div>
  );
}

export const hookSummary = (lead) => {
  const ws = lead.webhooks || [];
  if (!ws.length) return null;
  const bad = ws.filter((w) => w.status !== "delivered").length;
  return <span className={`sb-chip ${bad ? HOOK_STYLE.failed : HOOK_STYLE.delivered}`} data-testid={`lead-hooks-${lead.id}`}>Make {ws.length - bad}/{ws.length}</span>;
};
